// ============================================================================
// CardGrid.tsx — ЕДИНАЯ сетка карточек (карточный движок): одно место,
// где решается раскладка. Ориентир — РАЗМЕР КОНТЕЙНЕРА (ResizeObserver),
// не окно/устройство: ширина < 560px → ОДНА КОЛОНКА вплотную с разделителями
// (зазоры убираются), иначе — сетка с отступами. Используется ВЕЗДЕ, где
// рендерятся карточки (списки/формы/задачи) — дети получают narrow-флаг
// через render-prop, чтобы применить вид «разделитель» к самим карточкам.
// ============================================================================

import { useEffect, useRef, useState, type ReactNode } from 'react';

// Гистерезис: в одну колонку — при ширине < 540px, обратно — только при > 590px
export const CARD_GRID_NARROW_PX = 540;
export const CARD_GRID_WIDE_PX = 590;

export function CardGrid({
  children,
  className,
  containerClassName,
}: {
  children: (narrow: boolean) => ReactNode;
  className?: string;
  containerClassName?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [narrow, setNarrow] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? 0;
      if (w <= 0) return;
      setNarrow((prev) => (prev ? w < CARD_GRID_WIDE_PX : w < CARD_GRID_NARROW_PX));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className={narrow ? `flex-1 min-h-0 overflow-auto ${containerClassName || ''}` : `flex-1 min-h-0 overflow-auto p-3 ${containerClassName || ''}`}>
      <div
        ref={ref}
        className={narrow
          ? `grid grid-cols-1 divide-y divide-border ${className || ''}`
          : `grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3 ${className || ''}`}
      >
        {children(narrow)}
      </div>
    </div>
  );
}

/** Класс карточки для «разделительного» режима (одна колонка). */
export function cardNarrowClass(narrow: boolean): string {
  return narrow ? 'rounded-none border-0 border-b border-border/60' : '';
}
